import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { useCurrentUser } from '@ingenico/react-section-utils';
import { CoreStatus } from '@ingenico/react-ui/core-status';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { ListViewItem, useRenderDefaultColumHeader } from '@ingenico/react-ui/datagrid';
import { getHumanizedFileSize10 } from '@ingenico/react-ui/file-handler';
import type { KebabMenuOption } from '@ingenico/react-ui/kebab-menu';
import { KebabMenu } from '@ingenico/react-ui/kebab-menu';
import { useUserDateTimeFormat } from '@ingenico/react-utils/hooks';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import type { ButtonProps } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { toArray, toString } from 'lodash-es';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../components/device-logs-modal/DeviceLogsModal.module.scss';
import { getDeviceLogsMapperOfStatus } from '../components/device-logs-modal/DeviceLogsModal.util';
import type { DeviceConnectionStatusEnum } from '../domain/models/deviceInfo.model';
import type { DeviceLog, DeviceLogFile, DeviceLogType } from '../model/deviceLogs.model';
import { DeviceLogStatus } from '../model/deviceLogs.model';
import { useDownloadDeviceLogFile } from './useDownloadDeviceLogFile.hook';

export interface UseDeviceLogsModalDatagridColumnsReturn {
  readonly columns: GridColDef[];
}

export interface UseDeviceLogsModalDatagridColumnsParams {
  readonly logicalDeviceId: string;
  readonly handleStopRecording: (logId: string) => void;
  readonly handleCancelRequest: (logId: string) => void;
  readonly hasLogcatWritePermission?: boolean;
  readonly connectionStatus?: DeviceConnectionStatusEnum;
}

export function useDeviceLogsModalDatagridColumns({
  logicalDeviceId,
  handleStopRecording,
  handleCancelRequest,
  hasLogcatWritePermission,
  connectionStatus,
}: UseDeviceLogsModalDatagridColumnsParams): UseDeviceLogsModalDatagridColumnsReturn {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON, ALL_LNGS_NAMESPACES.LOGICAL_DEVICE]);

  const { defaultRenderColumnHeader } = useRenderDefaultColumHeader(t);

  const currentUser = useCurrentUser();
  const { getUserDateTimeFormatWithTimezone } = useUserDateTimeFormat(currentUser.dateTimeFormat);

  const renderCellValue = useCallback((params: GridRenderCellParams<DeviceLog, string>): ReactNode => {
    switch (params.field) {
      case 'description': {
        return <ListViewItem primaryText={toString(params.value)} numRows={params.field === 'description' ? 2 : 1} />;
      }
      case 'duration': {
        return (
          <div className={styles.durationContainer}>
            <ListViewItem primaryText={toString(params.value)} />
          </div>
        );
      }
      default: {
        return <ListViewItem primaryText={toString(params.value)} />;
      }
    }
  }, []);

  const renderCellTypes = useCallback(
    (params: GridRenderCellParams<DeviceLog, DeviceLogType[]>): ReactNode => {
      const joinedTypes = params.value
        ?.map((type) => t(`deviceLogsModal.type.${type}`, { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }))
        .join(', ');

      return <ListViewItem primaryText={joinedTypes} />;
    },
    [t],
  );

  const renderCellDate = useCallback(
    (params: GridRenderCellParams<DeviceLog, Date>): ReactNode => {
      const displayedDate =
        params.value && dayjs(params.value).isValid()
          ? getUserDateTimeFormatWithTimezone({ timezone: currentUser.zoneInfo, instant: params.value })
          : undefined;
      const [datePart, timePart] = displayedDate?.split(' ') ?? [];
      return <ListViewItem primaryText={datePart} secondaryText={timePart} />;
    },
    [currentUser.zoneInfo, getUserDateTimeFormatWithTimezone],
  );

  const renderStatus = useCallback(
    (params: GridRenderCellParams<DeviceLog, DeviceLogStatus>): ReactNode => {
      const mapper = getDeviceLogsMapperOfStatus();
      const { labelKey, status } = params.value ? mapper[params.value] : mapper.DEFAULT;
      return (
        <CoreStatus
          label={t(labelKey, { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
          status={status}
          transparentBackground
          customColorsClassNames={{ label: styles.labelTextColor }}
        />
      );
    },
    [t],
  );

  const { download: downloadLogFile } = useDownloadDeviceLogFile();

  const getFileNameOption = useCallback(
    (fileName: string, size: number) => (
      <div className={styles.downloadFileLabel__container}>
        <div className={styles.downloadFileLabel__filename}>
          <CoreTypography customClassName={styles.textDefault} variant={CoreTypographyVariant.Body02Regular}>
            {fileName}
          </CoreTypography>
        </div>
        <div className={styles.downloadFileLabel__fileSize}>
          <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
            {getHumanizedFileSize10(size, t, 1)}
          </CoreTypography>
        </div>
      </div>
    ),
    [t],
  );

  const getFilesForDownload = useCallback(
    (files?: DeviceLogFile[]): KebabMenuOption[] => {
      return toArray(
        files?.map(({ logFileId, fileName, size }) => ({
          label: getFileNameOption(fileName, size),
          key: logFileId,
          onClick: () => downloadLogFile({ fileId: logFileId, filename: fileName, logicalDeviceId }),
        })),
      );
    },
    [downloadLogFile, getFileNameOption, logicalDeviceId],
  );

  const getInProgressActions = useCallback(
    (logId: string): KebabMenuOption[] =>
      hasLogcatWritePermission
        ? [
            {
              label: (
                <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
                  {t('deviceLogsModal.actions.stopRecording', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
                </CoreTypography>
              ),
              icon: <StopCircleOutlinedIcon className={styles.stopRecordingIcon} />,
              key: `stop-recording-action-${logId}`,
              onClick: () => handleStopRecording(logId),
              disabled: connectionStatus === undefined ? false : connectionStatus !== 'CONNECTED',
              tooltip:
                connectionStatus === 'CONNECTED'
                  ? undefined
                  : t('deviceLogsModal.tooltip.rtsDisconnected', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
            },
            {
              label: (
                <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
                  {t('deviceLogsModal.actions.cancelRequest', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
                </CoreTypography>
              ),
              icon: <CancelOutlinedIcon className={styles.cancelRequestIcon} />,
              key: `cancel-request-${logId}`,
              onClick: () => handleCancelRequest(logId),
            },
          ]
        : [],
    [connectionStatus, handleCancelRequest, handleStopRecording, hasLogcatWritePermission, t],
  );
  const getStoppingAction = useCallback(
    (logId: string): KebabMenuOption[] =>
      hasLogcatWritePermission
        ? [
            {
              label: (
                <CoreTypography variant={CoreTypographyVariant.Body02Regular}>
                  {t('deviceLogsModal.actions.cancelRequest', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
                </CoreTypography>
              ),
              icon: <CancelOutlinedIcon className={styles.cancelRequestIcon} />,
              key: `cancel-request-${logId}`,
              onClick: () => handleCancelRequest(logId),
            },
          ]
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectionStatus, handleCancelRequest, handleStopRecording, hasLogcatWritePermission, t],
  );

  const manageDownloadOptions = useCallback(
    ({ status, files, id: _id }: DeviceLog) => {
      switch (status) {
        case DeviceLogStatus.Completed: {
          return getFilesForDownload(files);
        }
        default: {
          return [];
        }
      }
    },
    [getFilesForDownload],
  );

  const getDownloadKebabButton = useCallback((props?: ButtonProps) => {
    return (
      <button {...props} type="button" className={styles.downloadFilesButton}>
        <DownloadOutlinedIcon />
      </button>
    );
  }, []);

  const renderActionsCell = useCallback(
    (params: GridRenderCellParams<DeviceLog>) => {
      const deviceStatus = params.row.status;
      const deviceLogId = params.row.id;

      switch (deviceStatus) {
        case DeviceLogStatus.StopInProgress: {
          return <KebabMenu options={getStoppingAction(deviceLogId)} />;
        }
        case DeviceLogStatus.Completed: {
          return (
            <KebabMenu
              customKebabButton={getDownloadKebabButton}
              options={manageDownloadOptions(params.row)}
              optionWrapperClassName={styles.downloadFileOptionWrapper}
            />
          );
        }
        case DeviceLogStatus.InProgress: {
          return <KebabMenu options={getInProgressActions(deviceLogId)} />;
        }
        case DeviceLogStatus.Failed: {
          return undefined;
        }
        default: {
          return undefined;
        }
      }
    },
    [getDownloadKebabButton, getInProgressActions, getStoppingAction, manageDownloadOptions],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'title',
        renderHeader: defaultRenderColumnHeader('deviceLogsModal.field.title', ALL_LNGS_NAMESPACES.LOGICAL_DEVICE),
        headerName: t('deviceLogsModal.field.title', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1.05,
        renderCell: renderCellValue,
        filterable: false,
      },
      {
        field: 'description',
        renderHeader: defaultRenderColumnHeader(
          'deviceLogsModal.field.description',
          ALL_LNGS_NAMESPACES.LOGICAL_DEVICE,
        ),
        headerName: t('deviceLogsModal.field.description', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1.3,
        renderCell: renderCellValue,
        filterable: false,
      },
      {
        field: 'startDate',
        renderHeader: defaultRenderColumnHeader('deviceLogsModal.field.startDate', ALL_LNGS_NAMESPACES.LOGICAL_DEVICE),
        headerName: t('deviceLogsModal.field.startDate', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        type: 'date',
        flex: 1,
        renderCell: renderCellDate,
        filterable: false,
      },
      {
        field: 'duration',
        renderHeader: defaultRenderColumnHeader('deviceLogsModal.field.duration', ALL_LNGS_NAMESPACES.LOGICAL_DEVICE),
        headerName: t('deviceLogsModal.field.duration', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1,
        renderCell: renderCellValue,
        filterable: false,
        sortable: false,
      },
      {
        field: 'types',
        renderHeader: defaultRenderColumnHeader('deviceLogsModal.field.types', ALL_LNGS_NAMESPACES.LOGICAL_DEVICE),
        headerName: t('deviceLogsModal.field.types', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1.35,
        renderCell: renderCellTypes,
        filterable: false,
      },
      {
        field: 'status',
        renderHeader: defaultRenderColumnHeader('deviceLogsModal.field.status', ALL_LNGS_NAMESPACES.LOGICAL_DEVICE),
        headerName: t('deviceLogsModal.field.status', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE }),
        flex: 1.2,
        renderCell: renderStatus,
        filterable: false,
        hideable: false,
      },
      {
        field: 'actions',
        type: 'actions',
        renderHeader: () => null,
        headerName: '',
        hideable: false,
        sortable: false,
        flex: 0.5,
        filterable: false,
        renderCell: renderActionsCell,
      },
    ],
    [defaultRenderColumnHeader, t, renderCellValue, renderCellDate, renderCellTypes, renderStatus, renderActionsCell],
  );

  return { columns };
}
